"use client";

import {
  useGetBusinessViaIdentifier,
  useGetSalonForBooking,
  useGetFaqs,
} from "@/lib/apollo/hooks/booking-edge";
import {
  useGetPublicSalonReviews,
  useGetPublicSalonPromos,
} from "@/lib/api/usePublicV2";
import { BookingProvider } from "@/contexts/BookingContext";
import BookingPageContent from "./BookingPageContent";
import { useEffect, useState } from "react";
import { Promo } from "@/types/booking";

interface BookingPageWrapperProps {
  businessId: string;
  branchId?: string;
}

export default function BookingPageWrapper({
  businessId,
  branchId,
}: BookingPageWrapperProps) {
  const [selectedSalonId, setSelectedSalonId] = useState<string>("");

  // Fetch business data
  const {
    data: businessData,
    loading: businessLoading,
    error: businessError,
  } = useGetBusinessViaIdentifier(businessId);

  // Extract business and salons
  const business = businessData?.business || null;
  const salons = business?.salons || [];

  // Auto-select salon based on branchId or prefer one with vouchers
  useEffect(() => {
    if (salons.length > 0) {
      if (branchId) {
        // If branchId is provided, find the salon by urlIdentifier or id
        const targetSalon = salons.find(
          (s: any) => s.urlIdentifier === branchId || s.id === branchId
        );
        if (targetSalon) {
          setSelectedSalonId(targetSalon.id);
        }
      } else if (!selectedSalonId) {
        // Try to find a salon with vouchers (packages that are purchasable as vouchers)
        const salonWithVouchers = salons.find(
          (s: any) =>
            s.packages &&
            s.packages.some((pkg: any) => pkg.isPurchasableAsVoucher === true)
        );
        if (salonWithVouchers) {
          setSelectedSalonId(salonWithVouchers.id);
        } else {
          // Fallback to first salon
          setSelectedSalonId(salons[0].id);
        }
      }
    }
  }, [salons, branchId]);

  // Fetch salon-specific data (services are fetched here)
  const { data: salonData, loading: salonLoading } =
    useGetSalonForBooking(selectedSalonId);
  const { data: faqsData } = useGetFaqs(selectedSalonId);
  const { data: reviewsData, loading: reviewsLoading } =
    useGetPublicSalonReviews(selectedSalonId);
  const { data: promosData, loading: promosLoading } =
    useGetPublicSalonPromos(selectedSalonId);

  // Extract data
  const salonDetails = salonData?.salonForBooking || null;
  const services = (salonDetails?.services || []).filter(
    (service: any) => service.isPublic === true
  );

  // Extract vouchers - collect from ALL salons across the business, not just the selected one
  const allBusinessPackages = salons.flatMap((s: any) => s.packages || []);
  const packages = allBusinessPackages.filter(
    (pkg: any, index: number, self: any[]) =>
      pkg.isPurchasableAsVoucher === true &&
      self.findIndex((p: any) => p.id === pkg.id) === index
  );

  const faqs = faqsData?.faqs || [];
  const reviews = reviewsData || [];

  // Transform promos from API format to booking format
  const promos = (promosData || []).map((promo: any) => ({
    id: promo.id,
    title: promo.title || "",
    code: promo.code || promo.title, // Using title as code since API doesn't return code
    description: promo.description,
    discountType: promo.discountType as "percentage" | "fixed",
    discountValue: promo.discountValue,
    validFrom: promo.startAt,
    validTo: promo.endAt,
  }));

  // Merge salon data - enrich the selected salon with detailed data
  const enrichedSalons = salons.map((salon: any) => {
    if (salon.id === selectedSalonId && salonDetails) {
      // Merge all salon details from the API, keeping packages from original
      const enriched = {
        ...salon,
        ...salonDetails,
        // Explicitly keep packages from the original salon query
        packages: salon.packages || [],
        // Explicitly include services with staff pricing data
        services: salonDetails.services || [],
        // Include staff data
        salonStaffs: salonDetails.salonStaffs || [],
        // Include salon hours
        salonHours: salonDetails.salonHours || [],
      };
      return enriched;
    }
    return salon;
  });

  // Only block on business loading - services will load independently
  // This allows the page header and structure to display while services are still loading
  if (businessLoading) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (businessError) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading booking information</p>
          <p className="text-grey-600 text-sm">{businessError.message}</p>
        </div>
      </div>
    );
  }

  // No business found
  if (!business) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-lg mb-2">Business not found</p>
          <p className="text-grey-600">
            The business you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Process businessSpecialties - ensure it's an array of strings and format them
  const processedBusiness = {
    ...business,
    businessSpecialties:
      business.businessSpecialties?.map((specialty: any) => {
        // If specialty is already a string, use it
        if (typeof specialty === "string") return specialty;
        
        // Prefer description if available, otherwise transform name
        if (specialty.description) return specialty.description;
        
        // Transform name: replace underscores with spaces and capitalize
        const name = specialty.name;
        return name ? name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : '';
      }).filter(Boolean) || [],
  };

  return (
    <BookingProvider
      businessInfo={processedBusiness}
      salons={enrichedSalons}
      services={services}
      packages={packages}
      faqs={faqs}
      reviews={reviews}
      promos={promos}
      initialSelectedSalonId={selectedSalonId}
      servicesLoading={salonLoading}
    >
      <BookingPageContent />
    </BookingProvider>
  );
}
